/**
 * Small task pill that lives in the V2 sidebar and can be dragged onto
 * a day cell or time slot. Identity for dnd-kit is `task:<id>` so onDragEnd
 * can tell the drag payload apart from existing-event drags.
 */

import React, { useEffect, useRef, useCallback } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { Clock, MapPin, GripVertical } from 'lucide-react'
import type { Task } from '../../services/taskService'

export const TASK_DRAG_TYPE = 'task'

export interface TaskDragData {
  kind: typeof TASK_DRAG_TYPE
  taskId: number
  task: Task
}

interface DraggableTaskCardProps {
  task: Task
  // Optional: when a task is being dragged elsewhere we dim the original
  // card. Controlled from the parent to keep the DragOverlay logic in one
  // place (avoids ghost-vs-real double-render).
  isBeingDragged?: boolean
  /** Ring + scroll into view: this is the task the user came to place. */
  highlighted?: boolean
}

export const DraggableTaskCard: React.FC<DraggableTaskCardProps> = ({
  task,
  isBeingDragged = false,
  highlighted = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `task:${task.id}`,
    data: { kind: TASK_DRAG_TYPE, taskId: task.id, task } satisfies TaskDragData,
  })
  const elRef = useRef<HTMLDivElement | null>(null)
  const setRefs = useCallback((el: HTMLDivElement | null) => {
    setNodeRef(el)
    elRef.current = el
  }, [setNodeRef])
  useEffect(() => {
    if (highlighted) elRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [highlighted])

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isBeingDragged || isDragging ? 0.4 : 1,
  }

  const hasLocation = task.place_id != null
  const duration = task.estimated_duration ?? 30
  const priorityColor =
    task.priority === 'urgent' || task.priority === 'high'
      ? 'border-l-bad'
      : task.priority === 'medium'
        ? 'border-l-warn'
        : 'border-l-soft'

  return (
    <div
      ref={setRefs}
      style={style}
      data-highlighted={highlighted || undefined}
      className={`group flex items-start gap-2 rounded-md border border-soft ${priorityColor} border-l-4 bg-surface p-2 text-sm shadow-sm transition-shadow hover:shadow-md ${highlighted ? 'ring-2 ring-brand ring-offset-1' : ''}`}
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        aria-label={`Drag ${task.title}`}
        className="mt-0.5 cursor-grab touch-none text-muted hover:text-primary active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="truncate font-medium text-primary">{task.title}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {duration}m
          </span>
          {hasLocation && (
            <span className="inline-flex items-center gap-1 text-brand-primary">
              <MapPin className="h-3 w-3" />
              place
            </span>
          )}
          {task.due_date && (
            <span className="text-muted">due {task.due_date.slice(0, 10)}</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default DraggableTaskCard
